import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["access", "view"];
import React from 'react';
import cx from 'classnames';
import map from 'lodash/map';
import { observer } from 'mobx-react';
import ContentLoading from '../../../../components/ContentLoading';
import ContentError from '../../../../components/ContentError';
import Button from '../../../../components/Button';
import { Dropdown, DropdownItem, DropdownItemIcon, DropdownSection, DropdownSectionTitle } from '../../../../components/Dropdown';
import Icon from '../../../../components/Icon';
import IconButton from '../../../../components/IconButton';
import Avatar from '../../../../components/Avatar';
import styles from './Access.m.sass';
import { translate as T } from '../../../../utils/translate';
var Access = function Access(_ref) {
  var view = _ref.view;
  return view.fetchAccessInfo.match({
    pending: function pending() {
      return /*#__PURE__*/React.createElement(ContentLoading, null);
    },
    rejected: function rejected(err) {
      return /*#__PURE__*/React.createElement(ContentError, {
        error: err
      });
    },
    resolved: function resolved() {
      var jobAccesses = view.document.accesses.filter(function (x) {
        return x.type === 'Job';
      });
      return /*#__PURE__*/React.createElement("div", {
        className: cx(styles.root)
      }, /*#__PURE__*/React.createElement(AccessSection, {
        title: "Jobs",
        description: "Jobs that this document has been linked to."
      }, jobAccesses.length === 0 ? /*#__PURE__*/React.createElement(NoAccesses, {
        title: "Job Accesses"
      }, "You can link documents to jobs so everyone working on the job can access it, as well as make changes (if you allow it).") : jobAccesses.map(function (access) {
        return /*#__PURE__*/React.createElement(JobAccessItem, {
          title: access.job ? access.job.name : '<Unknown Job>',
          key: access.id,
          access: access,
          view: view
        });
      })));
    }
  });
};
var AccessSection = function AccessSection(_ref2) {
  var title = _ref2.title,
    description = _ref2.description,
    children = _ref2.children;
  return /*#__PURE__*/React.createElement("div", {
    className: styles.accessSection
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.accessSectionHeader
  }, /*#__PURE__*/React.createElement("p", {
    className: styles.accessSectionTitle,
    title: description
  }, title)), /*#__PURE__*/React.createElement("div", {
    className: styles.accesses
  }, children));
};
var NoAccesses = function NoAccesses(_ref3) {
  var title = _ref3.title,
    children = _ref3.children;
  return /*#__PURE__*/React.createElement("div", {
    className: styles.noAccesses
  }, /*#__PURE__*/React.createElement("p", {
    className: styles.noAccessesTitle
  }, title), /*#__PURE__*/React.createElement("p", {
    className: styles.noAccessesDescription
  }, children));
};
var jobPermissionDescription = {
  Owner: {
    title: 'Just me',
    description: 'Only you will be able to make changes.'
  },
  Clients: {
    title: T('Web.Common.Clients', 'Clients'),
    description: "Only ".concat(T('Web.Common.Clients', 'Clients'), " on this job can make changes."),
    disabled: true
  },
  Everyone: {
    title: 'Everyone',
    description: 'Any person on this job can make changes.'
  },
  Unknown: {
    title: 'Unspecified',
    disabled: true
  }
};
var JobAccessItem = observer(function (_ref4) {
  var access = _ref4.access,
    view = _ref4.view,
    props = _objectWithoutProperties(_ref4, _excluded);
  var current = jobPermissionDescription[access.editPermission] || jobPermissionDescription.Unknown;
  return /*#__PURE__*/React.createElement(AccessItem, _extends({
    access: access,
    view: view
  }, props), /*#__PURE__*/React.createElement(Dropdown, {
    position: "right",
    centerDropdown: true,
    disabled: view.busy,
    button: function button(props) {
      return /*#__PURE__*/React.createElement(Button, _extends({
        className: styles.accessButton,
        disabled: view.busy,
        loading: view.busy,
        title: current.description,
        size: "small"
      }, props, {
        iconRight: /*#__PURE__*/React.createElement(Icon, {
          small: true,
          material: true,
          name: "keyboard arrow down"
        })
      }), current.title);
    }
  }, /*#__PURE__*/React.createElement(DropdownSection, null, /*#__PURE__*/React.createElement(DropdownSectionTitle, null, "Who can edit this document?"), map(jobPermissionDescription, function (opts, key) {
    return !opts.disabled && /*#__PURE__*/React.createElement(DropdownItem, {
      key: key,
      onClick: function onClick() {
        return view.changeAccessPermission(access, key);
      }
    }, /*#__PURE__*/React.createElement(DropdownItemIcon, null, /*#__PURE__*/React.createElement(Icon, {
      small: true,
      material: true,
      name: key === access.editPermission ? 'radio button checked' : 'radio button unchecked',
      className: styles.permissionIcon
    })), /*#__PURE__*/React.createElement("div", {
      className: styles.permission
    }, /*#__PURE__*/React.createElement("p", {
      className: styles.permissionTitle
    }, opts.title), /*#__PURE__*/React.createElement("p", {
      className: styles.permissionSubtitle
    }, opts.description)));
  }).filter(function (x) {
    return x;
  }))));
});
var AccessItem = observer(function (_ref5) {
  var title = _ref5.title,
    access = _ref5.access,
    view = _ref5.view,
    children = _ref5.children;
  return /*#__PURE__*/React.createElement("div", {
    className: styles.accessItem
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.accessAvatar
  }, /*#__PURE__*/React.createElement(Avatar, {
    url: access.grantor.avatar,
    fallback: access.grantor.avatarFallback,
    title: access.grantor.displayName,
    size: 35
  })), /*#__PURE__*/React.createElement("div", {
    className: styles.accessInfo
  }, /*#__PURE__*/React.createElement("p", {
    className: styles.accessTitle
  }, title), /*#__PURE__*/React.createElement("p", {
    className: styles.accessSubtitle,
    title: "Access created by ".concat(access.grantor.displayName)
  }, "Granted by", ' ', /*#__PURE__*/React.createElement("span", {
    className: styles.accessGrantor
  }, access.grantor.isCurrentUser ? 'you' : access.grantor.displayName))), /*#__PURE__*/React.createElement("div", {
    className: styles.accessContent
  }, children), /*#__PURE__*/React.createElement("div", {
    className: styles.accessActions
  }, /*#__PURE__*/React.createElement(IconButton, {
    disabled: view.busy,
    onClick: function onClick() {
      return view.deleteAccess(access);
    },
    title: "Remove this access"
  }, /*#__PURE__*/React.createElement(Icon, {
    className: styles.removeIcon,
    auto: true,
    material: true,
    name: "remove circle outline"
  }))));
});
export default observer(Access);
//# sourceMappingURL=index.js.map
